import {
    BIDDING_MANAGER_READ_ROLES,
    BIDDING_READ_ROLES,
    BOOKING_DASHBOARD_ROLES,
    BUDGET_READ_ROLES,
    FORECASTS_READ_ROLES,
    LOUVRE_READ_ROLES,
    MELDUNGEN_READ_ROLES,
    Roles,
} from '@callmart/types';
import {
    AccountBalance as AccountBalanceIcon,
    BarChart as BarChartIcon,
    Dashboard as DashboardIcon,
    DashboardOutlined as DashboardOutlinedIcon,
    Event as EventIcon,
    Groups as GroupsIcon,
    Layers as LayersIcon,
    LocalPhoneOutlined as LocalPhoneOutlinedIcon,
    OnlinePrediction as OnlinePredictionIcon,
    Public as PublicIcon,
    Settings as SettingsIcon,
    ShoppingCartOutlined as ShoppingCartOutlinedIcon,
    TrendingUp as TrendingUpIcon,
    ViewList as ViewListIcon,
} from '@mui/icons-material';
import { budgetEnabled, louvreEnabled } from '../../config';
import { BudgetRoutes } from '../../enums/BudgetRoutes';
import { MenuItemNode } from './MenuItem';

// TODO: change to enumRoutes
export const _links: MenuItemNode[] = [
    {
        text: 'Dashboard',
        icon: DashboardOutlinedIcon,
        path: '/dashboard',
        role: Roles.CallmartWFM,
        value: 'dashboard',
    },
    {
        text: 'Dashboard',
        icon: DashboardIcon,
        path: '/dashboard',
        role: Roles.CallmartOSP,
        value: 'ospDashboard',
    },
    {
        text: 'Buchung aktueller Monat',
        icon: ShoppingCartOutlinedIcon,
        role: '',
        value: 'allBookings',
        nestedItems: [
            {
                text: 'Call Buchung',
                icon: ShoppingCartOutlinedIcon,
                path: '/ospBookings',
                role: Roles.CallmartOSP,
                value: 'ospBookings',
            },
            {
                text: 'Calldeltas',
                icon: TrendingUpIcon,
                path: '/calldeltaOverview',
                roles: BOOKING_DASHBOARD_ROLES,
                value: 'calldeltas',
            },
            {
                text: 'OSP Buchungen',
                icon: ViewListIcon,
                path: '/dailyOSPBookings',
                roles: BOOKING_DASHBOARD_ROLES,
                value: 'dailyOSPBookings',
            },
        ],
    },
    {
        text: 'Meldungen',
        icon: LocalPhoneOutlinedIcon,
        role: '',
        value: 'allDeltas',
        nestedItems: [
            {
                text: 'Delta Meldung',
                icon: LocalPhoneOutlinedIcon,
                path: '/locationDeltas',
                roles: MELDUNGEN_READ_ROLES,
                value: 'locationDeltas',
            },
            {
                text: 'Potenzialmeldung',
                icon: LayersIcon,
                path: '/capacityReport',
                roles: MELDUNGEN_READ_ROLES,
                value: 'capacityReport',
            },
            {
                text: 'IST Meldung',
                icon: LocalPhoneOutlinedIcon,
                path: '/performedCapacityReport',
                roles: MELDUNGEN_READ_ROLES,
                value: 'performedCapacityReport',
            },
            {
                text: 'Location Events',
                icon: EventIcon,
                path: '/events',
                roles: MELDUNGEN_READ_ROLES,
                value: 'events',
            },
        ],
    },
    {
        text: 'Bidding',
        icon: TrendingUpIcon,
        role: '',
        value: 'bidding',
        nestedItems: [
            {
                text: 'Übersicht',
                path: '/volume',
                roles: BIDDING_MANAGER_READ_ROLES,
                value: 'volumeOverview',
            },
            {
                text: 'Strategisch',
                path: '/volume/strategic',
                roles: BIDDING_READ_ROLES,
                value: 'strategicVolume',
            },
            // CALL-809: next to the released volumes it checks ("its own menu item under Strategic",
            // 4 Dec 2025); the old /forecasts/louvre URLs redirect here. OSP is excluded by role.
            ...(louvreEnabled
                ? [
                      {
                          text: 'Louvre',
                          path: '/louvre',
                          roles: LOUVRE_READ_ROLES,
                          value: 'louvreForecast',
                      },
                  ]
                : []),
            {
                text: 'Offered',
                path: '/volume/offered',
                roles: BIDDING_MANAGER_READ_ROLES,
                value: 'offeredVolume',
            },
            {
                text: 'Abschläge',
                path: '/reduction',
                role: Roles.CallmartWFM,
                value: 'reduction',
            },
            {
                text: 'Calculated',
                path: '/bidding-calculated',
                roles: BIDDING_MANAGER_READ_ROLES,
                value: 'biddingCalculated',
            },
            {
                text: 'Vorschlag',
                path: '/bidding-calculated/totals',
                role: Roles.CallmartOSP,
                value: 'calculatedTotals',
            },
        ],
    },
    {
        text: 'WFM',
        icon: GroupsIcon,
        path: '/WFM',
        role: Roles.CallmartWFM,
        value: 'WFM',
    },
    {
        text: 'Capacity Analysis',
        icon: BarChartIcon,
        path: '/capacityAnalysis',
        role: Roles.CallmartWFM,
        value: 'capacityAnalysis',
    },
    {
        text: 'Forecasts',
        icon: OnlinePredictionIcon,
        path: '',
        roles: FORECASTS_READ_ROLES,
        value: 'forecasts',
        nestedItems: [
            {
                text: 'Forecasts',
                path: '/forecasts',
                roles: FORECASTS_READ_ROLES,
                value: 'listForecast',
            },
            {
                text: 'New Forecast',
                path: '/forecasts/new',
                role: Roles.CallmartForecast,
                value: 'newForecast',
            },
            {
                text: 'Events & Adjustments',
                path: '/forecasts/events-adjustments',
                role: Roles.CallmartForecast,
                value: 'eventsAdjustmentsForecast',
            },
            {
                text: 'Operative Overview',
                path: '/forecasts/operative',
                role: Roles.CallmartForecast,
                value: 'operativeForecast',
            },
            {
                text: 'Strategic Overview',
                path: '/forecasts/strategic',
                roles: FORECASTS_READ_ROLES,
                value: 'strategicForecast',
            },
            {
                text: 'Data Management',
                path: '/forecasts/management',
                role: Roles.CallmartForecast,
                value: 'dataManagementForecast',
            },
        ],
    },
    ...(budgetEnabled
        ? [
              {
                  text: 'Budget',
                  icon: AccountBalanceIcon,
                  roles: BUDGET_READ_ROLES,
                  value: 'budget',
                  nestedItems: [
                      {
                          text: 'Overview',
                          path: BudgetRoutes.OVERVIEW,
                          roles: BUDGET_READ_ROLES,
                          value: 'budgetOverview',
                      },
                      {
                          text: 'Prices & ABT',
                          path: BudgetRoutes.PRICES_ABT,
                          roles: BUDGET_READ_ROLES,
                          value: 'budgetPricesAbt',
                      },
                      {
                          text: 'Scenarios',
                          path: BudgetRoutes.SCENARIOS,
                          roles: BUDGET_READ_ROLES,
                          value: 'budgetScenarios',
                      },
                      {
                          text: 'Billing Config',
                          path: BudgetRoutes.BILLING_CONFIG,
                          roles: BUDGET_READ_ROLES,
                          value: 'budgetBillingConfig',
                      },
                      {
                          text: 'Working Units',
                          path: BudgetRoutes.WORKING_UNIT_MAPPING,
                          roles: BUDGET_READ_ROLES,
                          value: 'budgetWorkingUnitMapping',
                      },
                  ],
              } satisfies MenuItemNode,
          ]
        : []),
    {
        text: 'Verwaltung',
        // WFM owns Verwaltung; Admin keeps the parent visible so the API Keys
        // sub-item is reachable for non-WFM admins (matches the route gate).
        roles: [Roles.CallmartWFM, Roles.Admin],
        icon: SettingsIcon,
        value: 'settings',
        nestedItems: [
            {
                text: 'Standorte',
                icon: PublicIcon,
                path: '/locations',
                role: Roles.CallmartWFM,
                value: 'locationSettings',
            },
            {
                text: 'Forecasters',
                icon: PublicIcon,
                path: '/forecasters',
                role: Roles.CallmartWFM,
                value: 'forecasterSettings',
            },
            {
                text: 'Worktypes',
                icon: PublicIcon,
                path: '/worktypes',
                role: Roles.CallmartWFM,
                value: 'worktypeSettings',
            },
            {
                text: 'Tasks',
                icon: PublicIcon,
                path: '/tasks',
                role: Roles.CallmartWFM,
                value: 'taskSettings',
            },
            {
                text: 'Capacity Types',
                icon: PublicIcon,
                path: '/capacityTypes',
                role: Roles.CallmartWFM,
                value: 'capacityTypeSettings',
            },
            {
                text: 'Channels',
                icon: PublicIcon,
                path: '/channels',
                role: Roles.CallmartWFM,
                value: 'channelSettings',
            },
            {
                text: 'Working Units',
                icon: PublicIcon,
                path: '/workingUnits',
                role: Roles.CallmartWFM,
                value: 'workingUnitSettings',
            },
            {
                text: 'Capacities',
                icon: PublicIcon,
                path: '/capacities',
                role: Roles.CallmartWFM,
                value: 'capacitySettings',
            },
            {
                text: 'Strategic Volumes',
                icon: PublicIcon,
                path: '/strategic-volumes',
                role: Roles.CallmartWFM,
                value: 'strategicVolumeSettings',
            },
            {
                text: 'Öffnungszeiten',
                icon: PublicIcon,
                path: '/workingHours',
                role: Roles.CallmartWFM,
                value: 'workingHoursSettings',
            },
            {
                text: 'Brands',
                icon: PublicIcon,
                path: '/brands',
                role: Roles.CallmartWFM,
                value: 'brandsSettings',
            },
            {
                text: 'Feiertage',
                icon: PublicIcon,
                path: '/holidays',
                role: Roles.CallmartWFM,
                value: 'holidaysSettings',
            },
            {
                text: 'API Keys',
                icon: SettingsIcon,
                path: '/apiKeys',
                role: Roles.Admin,
                permissions: ['manage:auth0'],
                value: 'apiKeysSettings',
            },
        ],
    },
    /*** commented out until feature release
     {
     text: '30 min Potentiale',
     icon: UpdateIcon,
     path: '/potentials',
     role: '',
     },*/
];
